import axios from 'axios';
import * as actions from './AuthActions';
import { backend_server_address } from '../../../backendServer';
import { clearOfflineCache, loadProfile, saveProfile } from '../../../capacitor/offlineCache';
import { isAccessSettled, onRefreshOutcome, recordAccess } from '../../../http/accessToken';

export const authStart = () => {
    return {
        type: actions.AUTH_START,
        waiting_authentication: true,
        token_access_expiration: null,
        authenticated: false,
        error: null,
        changePasswordStarted: false,
        recoverPasswordSuccess: null,
        loading: false,
        sso_provider: null,
    };
};

export const authSuccess = (data, username) => {
    return {
        type: actions.AUTH_SUCCESS,
        token_access_expiration: data.access_exp,
        error: null,
        loading: false,
        authenticated: true,
        // User info
        username: data.username,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        node_id: data.node,
        nodes: data.nodes,
        group: data.group,
        is_maper_employee: data.is_maper_employee,
        permissions: data.permissions,
        notification_preferences: data.notification_preferences,
        user_id: data.id,
        language: data.language,
        waiting_authentication: false,
        connection_error: false,
        sso_provider: null,
    };
};

export const authRefresh = (data) => {
    return {
        waiting_authentication: false,
        connection_error: false,
        type: actions.AUTH_REFRESH,
        token_access_expiration: data.access_exp,
        authenticated: true,
        // User info
        username: data.username,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        node_id: data.node,
        nodes: data.nodes,
        group: data.group,
        is_maper_employee: data.is_maper_employee,
        user_id: data.id,
        permissions: data.permissions,
        notification_preferences: data.notification_preferences,
        language: data.language,
    };
};

export const authCleanError = () => {
    return {
        type: actions.AUTH_CLEAN_ERROR,
        error: null,
        sso_provider: null,
    };
};

export const authSSOFail = (errorMessage, ssoProvider) => {
    return {
        type: actions.AUTH_SSO_FAIL,
        token_access_expiration: null,
        error: errorMessage,
        authenticated: false,
        waiting_authentication: false,
        loading: false,
        connection_error: false,
        sso_provider: ssoProvider,
    };
};

export const authFail = (error) => {
    let connection_error = true;
    if (error.response?.status >= 400 && error.response.status < 500) {
        connection_error = false;
    }
    const errorMessage = error.response?.data?.detail ?? error.message;
    const action = {
        type: actions.AUTH_FAIL,
        token_access_expiration: null,
        error: errorMessage,
        waiting_authentication: false,
        loading: false,
        connection_error,
        sso_provider: null,
    };
    if (!connection_error) {
        action.authenticated = false;
    }
    return action;
};

export const logout = () => {
    return {
        type: actions.AUTH_LOGOUT,
        connection_error: false,
        token_access: null,
        token_access_expiration: null,
        token_refresh: null,
        token_refresh_expiration: null,
        authenticated: false,
        waiting_authentication: false,
    };
};

// Render from the saved profile while the first token refresh is in flight. A refresh that settles
// first wins, so a 401 already received is never overridden. loadProfile is empty on the web.
export const startFromSavedProfile = () => async (dispatch) => {
    const profile = await loadProfile();
    if (profile && !isAccessSettled()) dispatch(authRefresh(profile));
};

// The session (http/accessToken.ts) refreshes the token; the auth state follows each outcome.
export const connectAccessToken = (dispatch, getState) =>
    onRefreshOutcome(async (outcome) => {
        if (outcome.ok) {
            saveProfile(outcome.profile);
            dispatch(authRefresh(outcome.profile));
            return;
        }
        // With no response at all, start from the last saved profile. authFail then keeps
        // connection_error set, and the session retries the refresh until the network returns.
        if (!outcome.error.response && !getState().authReducers.authenticated) {
            const profile = await loadProfile();
            if (profile) dispatch(authRefresh(profile));
        }
        dispatch(authFail(outcome.error));
    });

export const authLogin = (username, password) => (dispatch) => {
    dispatch(authStart());
    axios
        .post(
            backend_server_address + '/api/login/token2/',
            {
                username: username,
                password: password,
            },
            {
                withCredentials: true,
            },
        )
        .then(async (res) => {
            await clearOfflineCache();
            saveProfile(res.data);
            recordAccess(res.data, res.headers.date ?? null);
            dispatch(authSuccess(res.data, username));
        })
        .catch((error) => {
            dispatch(authFail(error));
        });
};

export const recoverPasswordMail = () => {
    return {
        type: actions.RECOVER_PASSWORD_MAIL,
        recoverRequested: true,
    };
};

export const recoverPasswordError = (error) => {
    return {
        type: actions.RECOVER_PASSWORD_ERROR,
        error: error,
    };
};

export const recoverPasswordSuccess = () => {
    return {
        type: actions.RECOVER_PASSWORD_SUCCESS,
        changePasswordStarted: true,
        recoverPasswordSuccess: true,
    };
};

export const recoverPasswordFail = (error) => {
    return {
        type: actions.RECOVER_PASSWORD_FAIL,
        error: error,
        changePasswordStarted: true,
        recoverPasswordSuccess: false,
    };
};

export const recoverPassword = (email) => {
    return (dispatch) => {
        let data = { email: email };
        axios
            .post(backend_server_address + '/api/login/user/recover_password/', data)
            .then((res) => {
                dispatch(recoverPasswordMail());
            })
            .catch((error) => {
                dispatch(recoverPasswordError(error));
            });
    };
};

export const recoverPasswordChange = (newPassword, recoverToken) => {
    return (dispatch) => {
        let data = { new_password: newPassword, recover_token: recoverToken };
        axios
            .post(backend_server_address + '/api/login/user/recover_password/', data)
            .then((res) => {
                dispatch(recoverPasswordSuccess());
            })
            .catch((error) => {
                dispatch(recoverPasswordFail(error));
            });
    };
};
